/**
 * "Sposta…" on the booking page (OPS-1), pure: what the dialog starts from,
 * and the schedule change its fields come to, with only what changed in it.
 * The same PATCH as the Planning's drag (`timeline-reschedule.ts`), for a
 * finger, a keyboard, or a move past the window's end.
 */
import { romeInstant, romeNow } from '../time'
import type { RescheduleBody } from './timeline-reschedule'

/** The booking as it stands: its vehicle, if any, and its instants. */
export interface MoveCurrent {
  vehicleId: string | null
  startsAt: string
  endsAt: string
}

/** The dialog's fields: Rome wall-clock, as every date field of the app; '' keeps the vehicle. */
export interface MoveForm {
  vehicleId: string
  pickupDate: string
  pickupTime: string
  returnDate: string
  returnTime: string
}

/** Why the fields come to no change yet; each a `booking.move` message. */
export type MoveProblem = 'unchanged' | 'invalidDates' | 'returnBeforePickup'

/** What the dialog shows when it opens: the booking as it is, the vehicle kept. */
export function initialMoveForm(current: MoveCurrent): MoveForm {
  const start = romeNow(new Date(current.startsAt))
  const end = romeNow(new Date(current.endsAt))
  return {
    vehicleId: '',
    pickupDate: start.date,
    pickupTime: start.time,
    returnDate: end.date,
    returnTime: end.time,
  }
}

/**
 * The PATCH body the fields come to: the vehicle when another was chosen, each
 * instant when it differs from the booking's. Nothing to send, or a date the
 * app cannot read, or a return at or before the pick-up, is a problem instead:
 * the server would refuse the last two, and the first is not a move.
 */
export function moveBody(
  current: MoveCurrent,
  form: MoveForm,
): { body: RescheduleBody } | { problem: MoveProblem } {
  const startsAt = romeInstant(form.pickupDate, form.pickupTime)
  const endsAt = romeInstant(form.returnDate, form.returnTime)
  if (startsAt === null || endsAt === null) return { problem: 'invalidDates' }
  if (Date.parse(endsAt) <= Date.parse(startsAt)) return { problem: 'returnBeforePickup' }
  const body: RescheduleBody = {}
  if (form.vehicleId !== '' && form.vehicleId !== current.vehicleId) body.vehicleId = form.vehicleId
  if (startsAt !== new Date(current.startsAt).toISOString()) body.startsAt = startsAt
  if (endsAt !== new Date(current.endsAt).toISOString()) body.endsAt = endsAt
  if (Object.keys(body).length === 0) return { problem: 'unchanged' }
  return { body }
}
